import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, DataTable, Details, Dialog, IconButton, MetricTile, Progress,
  StatusChip, StepProgress, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { avvisiDelServer, useAvvisi, useIstanze } from '../lib/dati';
import { durata, faseContatto, nomePassoServer, quandoRelativo } from '../lib/attivita';
import { conLeCopieSotto } from '../lib/copie';
import { ETICHETTA, Nome, TONO_ISTANZA } from './instances';
import { AccessoSSH } from './accesso_ssh';
import { IPFissoServer } from './ip-fisso';
import { Back, History, Info, Plus, Warn } from '../icons';
import { Anello, CaricoSezione, Logo, LogoAvanzamento } from '../brand';
import { localeIntl, t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

const GB = 1024 ** 3;

function gb(bytes: number | undefined): string {
  return ((bytes ?? 0) / GB).toLocaleString(localeIntl(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Cosa non va su questo server, in cima alla sua schermata: un banner per
 * ogni avviso acceso, e uno solo per quelli che reggono da poco. Nella lista
 * la scheda dice che c'è qualcosa; qui si legge cosa.
 */
function AvvisiServer({ slug, serverId }: { slug: string; serverId: string }) {
  const q = useAvvisi(slug);
  const suoi = avvisiDelServer(q.data, serverId);
  // Che il server non si faccia sentire questa schermata lo dice già da sé,
  // dal vivo e con l'ora dell'ultimo contatto («Il server non si fa sentire»,
  // «Ancora nessun dato»): l'avviso ripetuto sopra sarebbe la stessa cosa
  // due volte. Nella lista resta, perché lì non c'è altro che lo dica.
  const accesi = suoi.accesi.filter((a) => a.rule !== 'agent_muto');
  const { inArrivo } = suoi;
  if (accesi.length === 0 && inArrivo.length === 0) return null;
  return (
    <>
      {accesi.map((a) => (
        <Banner key={a.rule + a.target} tone="error" title={a.title}>
          {a.message} {t('Dura da {quando}.', { quando: quandoRelativo(a.since) })}
        </Banner>
      ))}
      {inArrivo.length > 0 && (
        <Banner tone="info" title={inArrivo.length === 1 ? t('Da tenere d’occhio') : t('{n} da tenere d’occhio', { n: inArrivo.length })}>
          {inArrivo.map((a) => a.message).join(' ')} {t('Se regge, diventa un avviso e parte la notifica.')}
        </Banner>
      )}
    </>
  );
}

/**
 * I servizi che l'agent riporta, con il nome che una persona riconosce. Il
 * nome dell'unit resta nei dettagli tecnici: chi deve entrare in SSH lo trova,
 * chi guarda se «il database è acceso» non deve decifrarlo.
 */
function nomeServizio(unit: string): string {
  // Dentro la funzione e non a livello di modulo: i nomi seguono la lingua.
  const servizi: Array<[RegExp, string]> = [
    [/^postgresql@/, t('Database (PostgreSQL)')],
    [/^nginx/, t('Server web (nginx)')],
    [/^fail2ban/, t('Protezione dagli accessi indesiderati')],
    [/^odoo-agent/, t('Agente di monitoraggio')],
    [/^ssh/, t('Accesso sicuro (SSH)')],
  ];
  for (const [re, nome] of servizi) {
    if (re.test(unit)) return nome;
  }
  // «odoo-acme.service» è l'istanza acme: il nome è più utile della categoria.
  if (/^odoo-/.test(unit)) return `Odoo · ${unit.replace(/^odoo-/, '').replace(/\.service$/, '')}`;
  return unit.replace(/\.service$/, '');
}

export function ServerPage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/servers/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [dismettere, setDismettere] = useState(false);
  const permessi = usePermessiSu(slug, { server: id });

  const server = useQuery({
    queryKey: ['server', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/servers/{id}', { params: { path: { slug, id } } })),
    // Pronto ma con l'IP fisso in arrivo si guarda ancora: la consegna la
    // dice il server stesso, quando la richiesta si spegne.
    refetchInterval: (q) => (q.state.data?.status === 'ready' && !q.state.data?.failoverIpOrdered ? false : 3000),
  });

  // Il job più recente di questo server. Serve perché `jobId` arriva solo
  // nella risposta della creazione: lista e dettaglio non lo portano, quindi
  // senza questa chiamata un server fallito non avrebbe modo di dire perché.
  const job = useQuery({
    queryKey: ['server-job', slug, id],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/jobs', {
        params: { path: { slug }, query: { serverId: id, limit: 1 } },
      })),
    refetchInterval: (q) => {
      const j = q.state.data?.jobs?.[0];
      return j && (j.status === 'running' || j.status === 'queued') ? 5000 : false;
    },
  });

  const metriche = useQuery({
    queryKey: ['metrics', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/servers/{id}/metrics', { params: { path: { slug, id } } })),
    // L'agent riporta ogni minuto: interrogare più spesso ridarebbe lo stesso
    // campione. Su un server non ancora pronto l'agent non esiste affatto.
    enabled: server.data?.status === 'ready',
    refetchInterval: 60_000,
  });

  const istanze = useIstanze(slug);
  const mie = (istanze.data?.instances ?? []).filter((i) => i.serverId === id);

  // Riprendere non chiede da dove: gli step già fatti sono segnati sulla
  // macchina, quindi il worker riparte da last_step da solo. Finito, si va al
  // log — è la ragione per cui si preme il bottone.
  const riprova = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/servers/{id}/retry', { params: { path: { slug, id } } })),
    onSuccess: (srv) => {
      qc.invalidateQueries({ queryKey: ['servers', slug] });
      qc.invalidateQueries({ queryKey: ['server', slug, id] });
      avvisa(srv.status === 'ready' ? t('Aggiornamento avviato: si applicano solo i passi nuovi') : t('Installazione ripresa da dove si era fermata'));
      if (srv.jobId) {
        navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: srv.jobId } });
      }
    },
  });

  const dismetti = useMutation({
    mutationFn: () =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/servers/{id}', { params: { path: { slug, id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers', slug] });
      avvisa(s?.name ? t('{nome} non è più gestito da Molo', { nome: s.name }) : t('Il server non è più gestito da Molo'));
      navigate({ to: '/orgs/$slug/servers', params: { slug } });
    },
  });

  const s = server.data;
  const ultimoJob = job.data?.jobs?.[0];
  // River riprova: finché il job è vivo, "fallito" sul server significa
  // "l'ultimo tentativo è fallito", non "è finita". Dirlo evita di far
  // intervenire qualcuno su una cosa che si sta già rimettendo a posto.
  const staRiprovando =
    s?.status === 'error' && (ultimoJob?.status === 'running' || ultimoJob?.status === 'queued');
  const campioni = metriche.data?.samples ?? [];
  const ultimo = campioni[campioni.length - 1];
  const agent = metriche.data?.agent;
  const inLavorazione = s && s.status !== 'ready' && s.status !== 'error' && s.status !== 'released';

  const vaiAlJob = ultimoJob
    ? () => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: ultimoJob.id } })
    : undefined;

  return (
    <>
      <TopAppBar
        leading={
          <IconButton label={t('Torna ai server')} onClick={() => navigate({ to: '/orgs/$slug/servers', params: { slug } })}>
            <Back />
          </IconButton>
        }
        title={s?.name ?? '…'}
        subtitle={s ? (
          <span className="md-mono">
            {s.failoverIpv4 ? t('{ip} (IP fisso) · macchina {macchina}', { ip: s.failoverIpv4, macchina: s.ipv4 ?? '' }) : s.ipv4 || t('senza indirizzo')}
          </span>
        ) : undefined}
        actions={
          s && (
            <div className="md-stack__inline" style={{ alignItems: 'center' }}>
              <StatusChip status={s.status} />
              {s.status !== 'decommissioning' && s.status !== 'released' && (
                <ConPermesso ha={permessi} serve="server.delete">
                  <Button variant="text" onClick={() => setDismettere(true)}>{t('Scollega')}</Button>
                </ConPermesso>
              )}
            </div>
          )
        }
      />
      <CaricoSezione attiva={server.isPending} />
      <div className="md-content">
        {server.error && <Banner tone="error" title={t('Server non leggibile')}>{server.error.message}</Banner>}

        {s && (
          <div className="md-stack">
            <AvvisiServer slug={slug} serverId={s.id} />
            <IPFissoServer slug={slug} server={s} vaiAlJob={vaiAlJob} />
            {inLavorazione && (
              <Card variant="outlined">
                <div className="md-section-head">
                  <h2 className="md-title-medium">{t('Installazione in corso')}</h2>
                  {vaiAlJob && <Button variant="tonal" onClick={vaiAlJob}>{t('Segui passo per passo')}</Button>}
                </div>
                {s.status === 'installing_stack' && s.totalSteps > 0 ? (
                  // Il molo cresce coi passi veri: l'acqua arriva all'ultimo.
                  <div className="md-avanzamento">
                    <LogoAvanzamento step={s.progress ?? 0} total={s.totalSteps} size={56} />
                    <StepProgress
                      step={s.progress ?? 0}
                      total={s.totalSteps}
                      label={nomePassoServer(s.progress ?? 0, s.totalSteps)}
                    />
                  </div>
                ) : s.status === 'bootstrapping' ? (
                  // Marea: un'attesa di fondo che non dipende da noi.
                  <div className="md-attesa">
                    <Logo size={56} anim="marea" label={t('In attesa del comando')} />
                    <p className="md-body-medium">
                      {t('Aspetto che il comando venga eseguito sulla macchina. Appena la chiave è autorizzata, l’installazione parte da sola.')}
                    </p>
                  </div>
                ) : (
                  // Ormeggio: si sta stabilendo un contatto con la macchina.
                  <div className="md-attesa">
                    <Logo size={56} anim="ormeggio" label={t('Contatto in corso')} />
                    <p className="md-body-medium">
                      {s.status === 'verifying'
                        ? t('Verifica finale: controllo che tutto risponda.')
                        : t('{fase}. Cominciato {quando}: di solito ci vogliono pochi minuti.', {
                          fase: faseContatto(s.status, s.provider, s.failoverIpOrdered),
                          quando: quandoRelativo(s.createdAt),
                        })}
                    </p>
                  </div>
                )}
              </Card>
            )}

            {s.status === 'error' && (
              <Banner
                tone={staRiprovando ? 'info' : 'error'}
                icon={<Warn />}
                title={staRiprovando ? t('Un tentativo è fallito, ne sto facendo un altro') : t('L’installazione si è fermata')}
                action={
                  <div className="md-stack__inline">
                    {vaiAlJob && <Button variant="text" onClick={vaiAlJob}>{t('Vedi dove')}</Button>}
                    {/* Non mentre River sta già riprovando da solo: due
                        provisioning sulla stessa macchina non si fanno, e
                        l'hub rifiuterebbe comunque. */}
                    {!staRiprovando && (
                      <ConPermesso ha={permessi} serve="server.create">
                        <Button
                          // Contornato e non tonale: dentro un banner d'errore
                          // un riempimento `secondary-container` è fuori dalle
                          // coppie garantite da M3, e infatti si legge male.
                          variant="outlined"
                          icon={riprova.isPending ? <Anello /> : undefined}
                          disabled={riprova.isPending}
                          onClick={() => riprova.mutate()}
                        >
                          {riprova.isPending ? t('Riprendo…') : t('Riprendi')}
                        </Button>
                      </ConPermesso>
                    )}
                  </div>
                }
              >
                {riprova.error?.message ||
                  ultimoJob?.error ||
                  (staRiprovando
                    ? t('Il nuovo tentativo è in coda: riparte dal passo che non è riuscito.')
                    : t('Riprendendo, l’installazione riparte dal passo che non è riuscito, senza rifare gli altri.'))}
              </Banner>
            )}

            {s.status === 'ready' && s.totalSteps > (s.progress ?? 0) && (
              <Banner
                icon={<Info />}
                title={t('L’installazione ha passi nuovi')}
                action={
                  <ConPermesso ha={permessi} serve="server.create">
                    <Button
                      variant="outlined"
                      icon={riprova.isPending ? <Anello /> : undefined}
                      disabled={riprova.isPending}
                      onClick={() => riprova.mutate()}
                    >
                      {riprova.isPending ? t('Aggiorno…') : t('Aggiorna il server')}
                    </Button>
                  </ConPermesso>
                }
              >
                {t('Questo server ha ricevuto {fatti} passi su {totale}: i nuovi si applicano da soli, senza rifare gli altri.', { fatti: s.progress ?? 0, totale: s.totalSteps })}
                {riprova.error?.message ? ` ${riprova.error.message}` : ''}
              </Banner>
            )}

            {agent && !agent.connected && agent.enrolled && (
              <Banner tone="error" icon={<Warn />} title={t('Il server non si fa sentire')}>
                {t('L\'ultimo contatto è di {quando}.', { quando: agent.lastSeenAt ? quandoRelativo(agent.lastSeenAt) : t('data sconosciuta') })}{' '}
                {t('I numeri qui sotto sono fermi a quel momento.')}
              </Banner>
            )}

            {ultimo && (
              <div className="md-metric-grid">
                <MetricTile
                  label={t('Processore')}
                  value={`${ultimo.cpuPercent.toFixed(0)}%`}
                  spark={campioni.slice(-24).map((c) => c.cpuPercent)}
                />
                <MetricTile label={t('Memoria in uso')} value={gb(ultimo.memUsed)} unit={` ${t('di {totale} GB', { totale: gb(ultimo.memTotal) })}`} />
                <MetricTile label={t('Carico')} value={ultimo.load1.toFixed(2)} unit={` ${ultimo.load1 > 2 ? t('alto') : t('normale')}`} />
                <MetricTile label={t('Acceso da')} value={durata(ultimo.uptimeSeconds ?? 0)} />
              </div>
            )}

            {s.status === 'ready' && (
              <Card variant="outlined">
                <div className="md-section-head">
                  <h2 className="md-title-medium">{t('Istanze su questo server')}</h2>
                  <ConPermesso ha={permessi} serve="instance.create">
                    <Button
                      variant="tonal"
                      icon={<Plus />}
                      onClick={() => navigate({ to: '/orgs/$slug/instances', params: { slug }, search: { nuova: id } })}
                    >
                      {t('Nuova istanza')}
                    </Button>
                  </ConPermesso>
                </div>
                {istanze.isSuccess && mie.length === 0 && (
                  <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {t('Ancora nessuna. Il server è pronto per ospitarne una.')}
                  </p>
                )}
                {mie.length > 0 && (
                  <DataTable
                    columns={[
                      { key: 'nome', header: t('Istanza') },
                      { key: 'versione', header: 'Odoo' },
                      { key: 'stato', header: t('Stato') },
                    ]}
                    rowKey={(r) => String(r.id)}
                    onRowClick={(r) => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: String(r.id) } })}
                    rows={conLeCopieSotto(mie).map((i) => ({
                      id: i.id,
                      nome: <Nome istanza={i} />,
                      versione: i.odooMajor,
                      stato: <StatusChip status={i.status} label={ETICHETTA[i.status] && t(ETICHETTA[i.status]!)} tone={TONO_ISTANZA[i.status]} />,
                    }))}
                  />
                )}
              </Card>
            )}

            {ultimo?.disks && ultimo.disks.length > 0 && (
              <Card variant="outlined">
                <h2 className="md-title-medium">{t('Spazio su disco')}</h2>
                <div className="md-stack">
                  {ultimo.disks.map((d) => {
                    const pct = d.Total ? Math.round((d.Used / d.Total) * 100) : 0;
                    return (
                      <div key={d.Mount} className="md-stack" style={{ gap: 'var(--md-sys-spacing-1)' }}>
                        <div className="md-step-progress__label md-body-small">
                          <span>{d.Mount === '/' ? t('Disco principale') : d.Mount}</span>
                          <span>
                            {t('{usati} di {totale} GB', { usati: gb(d.Used), totale: gb(d.Total) })}
                            {pct >= 85 && <strong style={{ color: 'var(--md-sys-color-error)' }}> · {t('quasi pieno')}</strong>}
                          </span>
                        </div>
                        <Progress value={pct} aria-label={t('Spazio usato su {mount}', { mount: d.Mount })} />
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {ultimo?.units && ultimo.units.length > 0 && (
              <Card variant="outlined">
                <h2 className="md-title-medium">{t('Servizi')}</h2>
                <DataTable
                  columns={[
                    { key: 'nome', header: t('Servizio') },
                    { key: 'stato', header: t('Stato') },
                  ]}
                  rowKey={(r) => String(r.id)}
                  // `postgresql.service` è solo il contenitore dei cluster: dire
                  // «database acceso» due volte confonderebbe. Resta nei dettagli.
                  rows={ultimo.units.filter((u) => u.Name !== 'postgresql.service').map((u) => ({
                    id: u.Name,
                    nome: nomeServizio(u.Name),
                    stato: (
                      <StatusChip
                        status={u.ActiveState}
                        label={u.ActiveState === 'active' ? t('Acceso') : u.ActiveState === 'inactive' ? t('Spento') : u.ActiveState}
                        tone={u.ActiveState === 'active' ? 'success' : 'error'}
                      />
                    ),
                  }))}
                />
              </Card>
            )}

            {s.status === 'ready' && (
              <Card variant="outlined">
                <h2 className="md-title-medium">{t('Accesso SSH')}</h2>
                <AccessoSSH slug={slug} serverId={s.id} nomeServer={s.name} />
              </Card>
            )}

            {s.status === 'ready' && metriche.isSuccess && !ultimo && (
              <Banner icon={<Logo size={24} anim="marea" tone="inherit" />} title={t('Ancora nessun dato dal server')}>
                {t('L\'agente è installato ma non ha ancora mandato niente. Il primo aggiornamento arriva entro un minuto.')}
              </Banner>
            )}

            <Details summary={t('Dettagli tecnici')}>
              <dl className="md-dati">
                <div className="md-dato"><dt>{t('Identificativo')}</dt><dd className="md-mono">{s.id}</dd></div>
                <div className="md-dato"><dt>{t('Collegamento')}</dt><dd>{s.provider === 'custom_ssh' ? t('Macchina propria, via SSH') : s.provider}</dd></div>
                <div className="md-dato"><dt>{t('Collegato')}</dt><dd>{new Date(s.createdAt).toLocaleString(localeIntl())}</dd></div>
                {agent?.lastSeenAt && <div className="md-dato"><dt>{t('Ultimo contatto dell’agente')}</dt><dd>{new Date(agent.lastSeenAt).toLocaleString(localeIntl())}</dd></div>}
              </dl>
              {/* Il registro si apre per chiedere «cosa è successo a questo
                  server?»: da qui ci si arriva già con la risposta filtrata. */}
              <div className="md-stack__inline md-sezione">
                <Button variant="text" icon={<History />} onClick={() => navigate({ to: '/orgs/$slug/registro', params: { slug }, search: { risorsa: 'server', id: s.id } })}>
                  {t('Registro di questo server')}
                </Button>
              </div>
              {ultimo?.units && ultimo.units.length > 0 && (
                <DataTable
                  className="md-sezione"
                  columns={[
                    { key: 'unit', header: t('Unit di systemd') },
                    { key: 'stato', header: t('Stato') },
                    { key: 'riavvii', header: t('Riavvii'), numeric: true },
                  ]}
                  rowKey={(r) => String(r.unit)}
                  rows={ultimo.units.map((u) => ({
                    unit: <span className="md-mono">{u.Name}</span>,
                    stato: u.ActiveState,
                    riavvii: u.NRestarts,
                  }))}
                />
              )}
              {ultimo?.disks && ultimo.disks.length > 0 && (
                <DataTable
                  className="md-sezione"
                  columns={[
                    { key: 'mount', header: t('Punto di mount') },
                    { key: 'uso', header: t('Uso'), numeric: true },
                    { key: 'inode', header: 'Inode', numeric: true },
                  ]}
                  rowKey={(r) => String(r.mount)}
                  rows={ultimo.disks.map((d) => ({
                    mount: <span className="md-mono">{d.Mount}</span>,
                    uso: `${gb(d.Used)} / ${gb(d.Total)} GB`,
                    inode: `${d.InodesUsedPercent.toFixed(0)}%`,
                  }))}
                />
              )}
              {vaiAlJob && (
                <div className="md-sezione">
                  <Button variant="text" onClick={vaiAlJob}>{t('Ultima attività su questo server')}</Button>
                </div>
              )}
            </Details>
          </div>
        )}
      </div>

      {/* Su una macchina collegata l'hub smette di gestirla, non la distrugge.
          Va detto qui: senza, qualcuno penserebbe di aver cancellato un VPS che
          invece continua a esistere e a costare. */}
      <Dialog
        open={dismettere}
        onOpenChange={setDismettere}
        title={s?.name ? t('Scollegare {nome}?', { nome: s.name }) : t('Scollegare questo server?')}
        description={
          s?.provider === 'custom_ssh' ? (
            t('Molo smette di gestirlo e dimentica come entrarci. La macchina {evidenza}: continua a esistere presso il tuo fornitore, con tutto quello che c\'è sopra.', {
              evidenza: <strong>{t('non viene spenta né cancellata')}</strong>,
            })
          ) : (
            t('Il server viene rimosso e le sue risorse rilasciate presso il fornitore.')
          )
        }
        actions={
          <>
            <Button variant="text" onClick={() => setDismettere(false)}>{t('Annulla')}</Button>
            <Button variant="danger" icon={dismetti.isPending ? <Anello /> : undefined} disabled={dismetti.isPending} onClick={() => dismetti.mutate()}>
              {dismetti.isPending ? t('Scollego…') : t('Scollega')}
            </Button>
          </>
        }
      >
        {mie.length > 0 && (
          <Banner tone="error" title={mie.length === 1 ? t('Ospita un’istanza') : t('Ospita {n} istanze', { n: mie.length })}>
            {t('Restano accese sulla macchina, ma da qui non le vedrai più, non ne verranno fatti backup e i loro indirizzi di piattaforma vengono tolti dal DNS. I backup già fatti restano nella destinazione e si possono ripristinare su un altro server.')}
          </Banner>
        )}
        {dismetti.error && <Banner tone="error" title={t('Non scollegato')}>{dismetti.error.message}</Banner>}
      </Dialog>
    </>
  );
}
